import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import { Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MdClose } from 'react-icons/md';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import CheckBox from 'components/inputs/CheckBox';
import { chargingStation as chargingStationApi } from 'api/chargingStation';

const PAGE_LIMIT = 20;

const formatLocation = (station) => {
  const address = (station.address || '').trim();
  if (address) return address;
  return (
    [station.area, station.city, station.state, station.pincode]
      .map((v) => (v == null ? '' : String(v)).trim())
      .filter(Boolean)
      .join(', ') || '—'
  );
};

const AddPublicStationModal = ({ show, onClose, onSubmit, existingIds }) => {
  const { t } = useTranslation();

  const [stations, setStations] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});

  const fetchStations = useCallback(async (nextPage = 1, query = '', showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      // Reuse the same station source as the legacy "Station Access" dropdown.
      const resp = await chargingStationApi.getStationByCity({
        page: nextPage,
        limit: PAGE_LIMIT,
        name: query,
        filterTenant: false,
      });
      setStations(resp.data?.results || []);
      setTotalPages(resp.data?.totalPages || 1);
      setPage(resp.data?.page || nextPage);
    } catch (e) {
      toast.error('Failed to fetch public stations');
    }
    if (showLoader) setIsLoading(false);
  }, []);

  useEffect(() => {
    if (show) {
      setSelectedMap({});
      setSearch('');
      fetchStations(1, '');
    }
  }, [show, fetchStations]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchStations(1, value, false);
  };

  const toggleStation = (station) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[station.id]) {
        delete next[station.id];
      } else {
        next[station.id] = station;
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const selected = Object.values(selectedMap);
    if (selected.length === 0) {
      toast.warn('Select at least one station');
      return;
    }
    onSubmit(selected);
  };

  const selectedCount = Object.keys(selectedMap).length;
  const alreadyAdded = new Set(existingIds || []);

  if (!show) return null;

  return (
    <Modal show={show} centered size="lg" dialogClassName="fsa-modal-dialog" onHide={onClose}>
      <div className="customer-form-page__main fsa-modal">
        <Card>
          <div className="customer-form-header__block">
            <div className="customer-header-name">
              <span>{'Grant Public Station Access'}</span>
            </div>
            <div className="close___btn">
              <MdClose size={30} color="#BE210B" onClick={onClose} />
            </div>
          </div>
          <div className="customer-form-body__block">
            <div className="customer-form--block">
              <div className="fsa-modal__search">
                <input
                  type="text"
                  className="form-control"
                  placeholder={'Search by station name'}
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="fsa-modal__list">
                {isLoading ? (
                  <div className="loader--block" style={{ padding: '1.5rem', justifyContent: 'center' }}>
                    <Spinner as="span" animation="border" size="sm" role="status" />
                    <span className="ps-2">{t('placeHolder.loading')}</span>
                  </div>
                ) : _.isEmpty(stations) ? (
                  <div className="empty-data-block" style={{ padding: '1.5rem', textAlign: 'center' }}>
                    {'No public stations found'}
                  </div>
                ) : (
                  _.map(stations, (station) => {
                    const isAdded = alreadyAdded.has(station.id);
                    return (
                      <div
                        key={station.id}
                        className={`fsa-modal__row${selectedMap[station.id] ? ' fsa-modal__row--selected' : ''}`}
                        onClick={() => !isAdded && toggleStation(station)}
                        style={isAdded ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      >
                        <div className="fsa-modal__row-text">
                          <span className="fsa-modal__row-name">{station.name}</span>
                          <span className="fsa-modal__row-sub">
                            {isAdded ? 'Already added' : formatLocation(station)}
                          </span>
                        </div>
                        <span onClick={(e) => e.stopPropagation()}>
                          <CheckBox
                            checked={!!selectedMap[station.id]}
                            onChange={() => !isAdded && toggleStation(station)}
                          />
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="fsa-modal__footer">
                <span>{`${selectedCount} selected`}</span>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Button disabled={page <= 1} onClick={() => fetchStations(page - 1, search, false)}>
                      {'Prev'}
                    </Button>
                    <span style={{ minWidth: '70px', textAlign: 'center' }}>{`${page} / ${totalPages}`}</span>
                    <Button disabled={page >= totalPages} onClick={() => fetchStations(page + 1, search, false)}>
                      {'Next'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="fsa-modal__actions">
                <Button className="cancel_btn" onClick={onClose}>
                  {'Cancel'}
                </Button>
                <Button onClick={handleSubmit}>{'Grant Access'}</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

AddPublicStationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  existingIds: PropTypes.array,
};

export default AddPublicStationModal;
